import axios from "axios";
import SessionService from "./SessionService";
import React from "react";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api",
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function logout() {
  SessionService.clear();
}

axiosClient.interceptors.request.use(
  (config) => {
    const token = SessionService.accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject({ message: "Network error. Please try again." });
    }

    const status = error.response.status;

    if (status === 403) {
      logout();
      return Promise.reject({ message: "Session expired. Please log in again." });
    }

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosClient(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = SessionService.refreshToken;
        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        const { data } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/refresh-token`,
          { refreshToken }
        );

        if (data?.access_token) {
          SessionService.setSession({
            accessToken: data.access_token,
            refreshToken: data.refresh_token || refreshToken,
            user: SessionService.user,
          });

          onRefreshed(data.access_token);

          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return axiosClient(originalRequest);
        } else {
          logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (status >= 500) {
      return Promise.reject({
        message: "Server error. Please try again later.",
      });
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default axiosClient;
